/**
 * 展示层格式化工具。
 *
 * 纯函数、无依赖，前后端共用：Worker 用它拼动态流文案，前端用它渲染列表与卡片。
 */

export type Tone = 'neutral' | 'accent' | 'ok' | 'info' | 'caution' | 'warning' | 'critical';

const TONES: readonly Tone[] = ['neutral', 'accent', 'ok', 'info', 'caution', 'warning', 'critical'];

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** 相对时间：「刚刚」「12 分钟前」「3 天前」，超过 30 天回落到具体日期。 */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const at = new Date(iso);
  const time = at.getTime();
  if (Number.isNaN(time)) return '—';

  const diff = now.getTime() - time;
  if (diff < 0) return formatDateTime(iso);
  if (diff < MINUTE) return '刚刚';
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)} 分钟前`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)} 小时前`;
  if (diff < 30 * DAY) return `${Math.floor(diff / DAY)} 天前`;
  return formatDate(iso);
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/** 本地时区的 `YYYY-MM-DD HH:mm`。 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/** 本地时区的 `YYYY-MM-DD`。 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

/** `<input type="date">` 需要的 `YYYY-MM-DD`（本地时区）。 */
export function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

/** 截止日期是否已经逾期（当天不算逾期）。 */
export function isOverdue(iso: string | null | undefined, now: Date = new Date()): boolean {
  if (!iso) return false;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return false;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const due = new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
  return due < today;
}

export function truncate(text: string, max: number): string {
  const value = text.trim();
  if (value.length <= max) return value;
  return `${value.slice(0, Math.max(0, max - 1))}…`;
}

/** 列表里的一句话摘要：去掉换行、压缩空白。 */
export function inlineText(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * 标签配色：按标签内容做稳定散列，保证同一个标签在任何页面都是同一个颜色批次。
 * 色板里没有红色，因此标签不会与告警等级抢语义。
 */
export function tagTone(tag: string): Tone {
  let hash = 0;
  for (let index = 0; index < tag.length; index += 1) {
    hash = (hash * 31 + tag.charCodeAt(index)) % 100_000;
  }
  const palette: readonly Tone[] = ['accent', 'info', 'ok', 'caution', 'neutral', 'warning'];
  return palette[hash % palette.length] ?? 'neutral';
}

/** 评分归一化到 0–100，并夹到合法区间。 */
export function clampScore(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(100, Math.max(0, Math.round(parsed)));
}

/** 把 0–1 的比例渲染成百分比文案，`null` 表示样本不足。 */
export function formatRate(rate: number | null): string {
  if (rate === null || !Number.isFinite(rate)) return '—';
  return `${Math.round(rate * 100)}%`;
}

export function isTone(value: unknown): value is Tone {
  return typeof value === 'string' && (TONES as readonly string[]).includes(value);
}

/** 把任意服务端下发的字符串收敛到合法 tone，避免脏数据把样式打崩。 */
export function coerceTone(value: unknown, fallback: Tone = 'neutral'): Tone {
  return isTone(value) ? value : fallback;
}
